// The engine, in four steps. This is the overview that primes the four deep
// pillar sections below it (Ad Strategist, Fusion, Templates, AdPilot).
const STEPS = [
  {
    step: '01',
    icon: 'ri-search-eye-line',
    title: 'Research',
    description:
      "Ad Strategist reads your competitors' live ads, finds the gaps, and writes the copy and concepts built to beat them.",
  },
  {
    step: '02',
    icon: 'ri-magic-line',
    title: 'Create',
    description:
      'Fusion and our recipe library turn your product into on-brand, scroll-stopping ads. A fresh one every time, no designer.',
  },
  {
    step: '03',
    icon: 'ri-send-plane-line',
    title: 'Publish',
    description:
      'AdPilot schedules and posts to Facebook & Instagram on a steady cadence, so you stay in-feed with zero manual work.',
  },
  {
    step: '04',
    icon: 'ri-line-chart-line',
    title: 'Profit',
    description:
      'Sharper creative and smarter targeting mean more revenue per dollar. Customers are seeing up to 4x+ ROAS.',
  },
];

// Per-step accent so the four cards read as a distinct, energetic sequence.
// Full class strings kept literal so Tailwind's JIT picks them up.
const ACCENTS = [
  { grad: 'from-primary to-[#AB2164]', glow: 'shadow-primary/40', text: 'text-primary' },
  { grad: 'from-accent to-[#3a2ba6]', glow: 'shadow-accent/40', text: 'text-accent' },
  { grad: 'from-sky-500 to-sky-700', glow: 'shadow-sky-500/40', text: 'text-sky-600' },
  { grad: 'from-emerald-500 to-emerald-700', glow: 'shadow-emerald-500/40', text: 'text-emerald-600' },
];

export default function Features() {
  return (
    <section id="how-it-works" className="relative overflow-hidden py-16 md:py-32 bg-surface">
      {/* Soft background glows for depth */}
      <div className="pointer-events-none absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 w-[28rem] h-[28rem] rounded-full bg-accent/10 blur-3xl" aria-hidden="true" />

      <div className="relative w-full px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-20">
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-light text-accent text-xs font-semibold tracking-wider uppercase mb-6">
            <i className="ri-route-line" />
            How It Works
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark leading-[1.1] tracking-tight mb-5">
            One platform.
            <br />
            The whole{' '}
            <span className="bg-gradient-to-r from-primary via-accent to-sky-500 bg-clip-text text-transparent">
              ad engine
            </span>
            .
          </h2>
          <p className="text-dark-muted text-base md:text-lg leading-relaxed">
            Other tools do one piece. Ad Astronaut runs every step, from competitor
            research to a posted ad that performs.
          </p>
        </div>

        {/* Step Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-8 max-w-7xl mx-auto">
          {STEPS.map((s, index) => {
            const accent = ACCENTS[index % ACCENTS.length];
            return (
              <div
                key={s.step}
                className="group relative bg-white rounded-2xl md:rounded-3xl p-5 md:p-8 border border-surface-dark/30 shadow-[0_10px_35px_-15px_rgba(15,15,30,0.15)] hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden"
              >
                {/* Top accent bar */}
                <div className={`absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r ${accent.grad}`} />

                {/* Watermark step number */}
                <span
                  className={`absolute top-3 right-4 md:top-5 md:right-6 font-display text-4xl md:text-6xl font-black bg-gradient-to-br ${accent.grad} bg-clip-text text-transparent opacity-[0.16] group-hover:opacity-30 transition-opacity duration-500 select-none`}
                >
                  {s.step}
                </span>

                {/* Icon */}
                <div
                  className={`relative w-11 h-11 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br ${accent.grad} flex items-center justify-center mb-4 md:mb-6 shadow-lg ${accent.glow} group-hover:scale-110 group-hover:-rotate-3 transition-transform duration-500`}
                >
                  <i className={`${s.icon} text-lg md:text-2xl text-white`} />
                </div>

                {/* Content */}
                <h3 className="font-display text-base md:text-2xl font-bold text-dark mb-2 md:mb-3">
                  {s.title}
                </h3>
                <p className="text-xs md:text-base text-dark-muted leading-relaxed">
                  {s.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
